import React, { useState, useEffect, useRef } from "react";
import "./Extracurricular.css";

const activities = [
    {
        title: "Art & Craft",
        description: "Nurturing creative expression and fine motor skills through painting, drawing, sculpting, and tactile crafts.",
        image: "/extracurricular/art_craft.png",
        color: "#ffebd9",
        accent: "#ff8943"
    },
    {
        title: "Music & Movement",
        description: "Exploring rhythm, sound, and physical coordination through singing, interactive instruments, and dance.",
        image: "/extracurricular/music_movement.png",
        color: "#e8f5e9",
        accent: "#4caf50"
    },
    {
        title: "Yoga & Mindfulness",
        description: "Developing concentration, emotional balance, physical posture, and calm breathing exercises designed for kids.",
        image: "/extracurricular/yoga_mindfulness.png",
        color: "#e3f2fd",
        accent: "#2196f3"
    },
    {
        title: "Gardening & Nature",
        description: "Connecting with nature by planting seeds, watering gardens, and learning about biodiversity first-hand.",
        image: "/extracurricular/gardening_nature.png",
        color: "#fef8e7",
        accent: "#ffb300"
    }
];

const Extracurricular = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const timeoutRef = useRef(null);

    const resetTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    useEffect(() => {
        if (!isHovered) {
            resetTimeout();
            timeoutRef.current = setTimeout(
                () =>
                    setCurrentIndex((prevIndex) =>
                        prevIndex === activities.length - 1 ? 0 : prevIndex + 1
                    ),
                5000
            );
        }
        return () => {
            resetTimeout();
        };
    }, [currentIndex, isHovered]);

    const handleNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === activities.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handlePrev = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? activities.length - 1 : prevIndex - 1
        );
    };

    return (
        <section 
            className="extracurricular-section"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="extra-container">
                <div className="extra-header">
                    <span className="extra-tagline">Beyond the Curriculum</span>
                    <h2 className="extra-title">Extracurricular Activities</h2>
                    <p className="extra-desc">
                        We offer a diverse range of activities that encourage child-led exploration, 
                        physical health, and creative self-expression.
                    </p>
                </div>

                <div className="extra-carousel">
                    <div className="extra-carousel-inner">
                        {activities.map((activity, index) => {
                            let position = "nextSlide";
                            if (index === currentIndex) {
                                position = "activeSlide";
                            } else if (
                                index === currentIndex - 1 ||
                                (currentIndex === 0 && index === activities.length - 1)
                            ) {
                                position = "lastSlide";
                            }

                            return (
                                <article 
                                    className={`extra-slide ${position}`} 
                                    key={index}
                                    style={{ '--accent-color': activity.accent }}
                                >
                                    <div className="extra-slide-card" style={{ backgroundColor: activity.color }}>
                                        <div className="extra-slide-image-wrapper">
                                            <img src={activity.image} alt={activity.title} className="extra-slide-image" />
                                        </div>
                                        <div className="extra-slide-content">
                                            <h3 className="extra-slide-title">{activity.title}</h3>
                                            <p className="extra-slide-desc">{activity.description}</p>
                                            <div className="extra-slide-indicator">
                                                <span className="current">{index + 1}</span>
                                                <span className="separator">/</span>
                                                <span className="total">{activities.length}</span>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    <button className="extra-nav-btn prev" onClick={handlePrev} aria-label="Previous slide">
                        <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button className="extra-nav-btn next" onClick={handleNext} aria-label="Next slide">
                        <i className="fa-solid fa-chevron-right"></i>
                    </button>
                </div>

                <div className="extra-dots">
                    {activities.map((_, index) => (
                        <button
                            key={index}
                            className={`extra-dot ${index === currentIndex ? "active" : ""}`}
                            onClick={() => setCurrentIndex(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Extracurricular;
